/**
 * ImmersiveWorkoutExerciseInfo - Exercise details overlay
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { Theme } from '../../../../Theme/Theme';
import { ViewSet } from '../../../../Model/WorkoutLog/ViewSet';
import { useLanguage } from '../../../../Hooks/useLanguage';
import { useTrans } from '../../../../Store/System/LocalizationStore';

const Container = styled.div`
  padding: 20px 24px;
`;

const ExerciseName = styled.h2`
  color: #fff;
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 8px;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
`;

const SetInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
`;

const SetBadge = styled.div`
  background: rgba(255, 255, 255, 0.2);
  padding: 6px 12px;
  border-radius: 12px;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
`;

const MetricBadge = styled.div`
  background: ${Theme.Colors.primary}40;
  padding: 6px 12px;
  border-radius: 12px;
  color: ${Theme.Colors.primary};
  font-size: 13px;
  font-weight: 700;
`;

const TimerDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TimerValue = styled.span`
  color: #fff;
  font-size: 42px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
`;

const TimerLabel = styled.span`
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  font-weight: 500;
`;

export type ImmersiveWorkoutExerciseInfoProps = {
  viewSet: ViewSet;
  isDurationBased: boolean;
};

export const ImmersiveWorkoutExerciseInfo: React.FC<ImmersiveWorkoutExerciseInfoProps> = observer(
  ({ viewSet, isDurationBased }) => {
    const { t } = useTrans();
    const { language } = useLanguage();

    const exerciseName = viewSet.exercise.localeName(language);
    const setIndex = viewSet.viewBlock.sets.findIndex((s) => s.id === viewSet.id);
    const setNumber = setIndex >= 0 ? setIndex + 1 : 1;
    const totalSets = viewSet.viewBlock.sets.length;

    // Duration display
    const durationMs = viewSet.duration;
    const remainingMs = viewSet.remainingDuration;
    const plannedDurationMs = viewSet.plannedDurationMs;

    const formatTime = (ms: number) => {
      const totalSeconds = Math.floor(ms / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    // Show countdown for duration-based, elapsed for reps-based
    const displayTime = isDurationBased && plannedDurationMs > 0 
      ? formatTime(Math.max(0, remainingMs)) 
      : formatTime(durationMs);

    const timerLabel = isDurationBased && plannedDurationMs > 0 
      ? t('workout.remaining', { defaultValue: 'remaining' })
      : t('workout.elapsed', { defaultValue: 'elapsed' });

    return (
      <Container>
        <ExerciseName>{exerciseName}</ExerciseName>
        <SetInfo>
          <SetBadge>
            {t('workout.setOf', { defaultValue: 'Set {{current}} of {{total}}', current: setNumber, total: totalSets })}
          </SetBadge>
          {viewSet.plannedRepetitions && !isDurationBased && (
            <MetricBadge>{viewSet.plannedRepetitions} reps</MetricBadge>
          )}
          {viewSet.plannedWeight && (
            <MetricBadge>{viewSet.plannedWeight} kg</MetricBadge>
          )}
        </SetInfo>
        <TimerDisplay>
          <TimerValue>{displayTime}</TimerValue>
          <TimerLabel>{timerLabel}</TimerLabel>
        </TimerDisplay>
      </Container>
    );
  },
);
